(() => {
  'use strict';

  const existingLoader = window.TANProofStoriesLoader;
  if (existingLoader?.scan) {
    existingLoader.scan(document);
    return;
  }

  const ROOT_SELECTOR = 'tan-proof-stories[data-proof-runtime-src]';
  const ROOT_STATES = new WeakMap();
  const PREFETCHED_RUNTIMES = new Set();
  const SCROLL_QUIET_MS = 500;
  const STATS_FRAME_INTERVAL = 30;
  let runtimeRequest = null;

  const runtimeReady = () => Boolean(window.customElements?.get('tan-proof-stories'));
  const absoluteUrl = (value) => {
    try {
      return new URL(String(value || ''), document.baseURI).href;
    } catch (_error) {
      return '';
    }
  };

  const scrollEventTargets = () => {
    const targets = [window];
    const pageWrapper = document.querySelector('.page-wrapper');
    if (pageWrapper instanceof HTMLElement) targets.push(pageWrapper);
    return targets;
  };

  const prefetchRuntime = (root) => {
    const source = absoluteUrl(root.dataset.proofRuntimeSrc);
    if (!source || runtimeReady() || PREFETCHED_RUNTIMES.has(source)) return;
    const alreadyRequested = Array.from(document.querySelectorAll('script[src], link[href]')).some(
      (element) => absoluteUrl(element.src || element.href) === source
    );
    if (alreadyRequested) return;

    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.as = 'script';
    link.href = source;
    link.setAttribute('fetchpriority', 'low');
    link.dataset.tanProofRuntime = 'prefetch';
    link.addEventListener('error', () => {
      PREFETCHED_RUNTIMES.delete(source);
      link.remove();
    }, { once: true });
    PREFETCHED_RUNTIMES.add(source);
    document.head.append(link);
  };

  const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));
  const easeOutCubic = (value) => 1 - Math.pow(1 - value, 3);
  const statItems = (root) => Array.from(root.querySelectorAll('[data-proof-stat]')).map(
    (stat, index) => ({
      stat,
      number: stat.querySelector('[data-proof-number]'),
      target: clamp(Number(stat.dataset.proofTarget) || 0, 0, 100),
      delay: index * 120,
    })
  );
  const setStatValue = (item, value) => {
    const rounded = Math.round(value);
    item.stat.style.setProperty('--tan-proof-progress', String(value));
    if (item.number && item.number.textContent !== String(rounded)) {
      item.number.textContent = String(rounded);
    }
  };
  const setFinalStats = (items) => items.forEach((item) => setStatValue(item, item.target));

  const setupStats = (root, state) => {
    const items = statItems(root);
    if (!items.length) return;

    // Keep the counters in the lightweight loader. This lets them fill while
    // vertical scrolling continues without parsing/upgrading the reels runtime.
    root.dataset.proofStatsManaged = 'loader';
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (reducedMotion.matches || !('IntersectionObserver' in window)) {
      setFinalStats(items);
      return;
    }

    state.statsMotionQuery = reducedMotion;
    state.statsMotionHandler = (event) => {
      if (!event.matches) return;
      state.statsObserver?.disconnect();
      state.statsObserver = null;
      if (state.statsFrame) cancelAnimationFrame(state.statsFrame);
      state.statsFrame = 0;
      state.statsStarted = true;
      setFinalStats(items);
    };
    reducedMotion.addEventListener?.('change', state.statsMotionHandler);

    items.forEach((item) => setStatValue(item, 0));
    const stats = root.querySelector('.tan-story-proof__stats');
    if (!stats) return;

    const startAnimation = () => {
      if (state.statsStarted || state.abortController.signal.aborted) return;
      state.statsStarted = true;
      const duration = 1180;
      const startedAt = performance.now();
      let previousPaint = startedAt - STATS_FRAME_INTERVAL;

      const frame = (time) => {
        state.statsFrame = 0;
        if (state.abortController.signal.aborted || !root.isConnected) return;
        if (time - previousPaint < STATS_FRAME_INTERVAL) {
          state.statsFrame = requestAnimationFrame(frame);
          return;
        }
        previousPaint = time;
        let complete = true;
        items.forEach((item) => {
          const progress = clamp((time - startedAt - item.delay) / duration);
          if (progress < 1) complete = false;
          setStatValue(item, item.target * easeOutCubic(progress));
        });
        if (!complete) state.statsFrame = requestAnimationFrame(frame);
      };

      state.statsFrame = requestAnimationFrame(frame);
    };

    state.statsObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;
        state.statsObserver?.disconnect();
        state.statsObserver = null;
        startAnimation();
      },
      { threshold: 0.2, rootMargin: '0px 0px -10% 0px' }
    );
    state.statsObserver.observe(stats);
  };

  const loadRuntime = (root) => {
    if (runtimeReady()) return Promise.resolve();
    if (runtimeRequest) return runtimeRequest;

    const source = absoluteUrl(root.dataset.proofRuntimeSrc);
    if (!source) return Promise.reject(new Error('PROOF_STORIES_RUNTIME_URL_MISSING'));

    runtimeRequest = new Promise((resolve, reject) => {
      const pending = Array.from(document.scripts).find(
        (script) => absoluteUrl(script.src) === source
      );
      const script = pending || document.createElement('script');
      let timeoutId = 0;
      const cleanup = () => {
        window.clearTimeout(timeoutId);
        script.removeEventListener('load', handleLoad);
        script.removeEventListener('error', handleError);
      };
      const fail = (message) => {
        cleanup();
        if (!pending || script.dataset.tanProofRuntime === 'true') script.remove();
        runtimeRequest = null;
        reject(new Error(message));
      };
      const handleLoad = () => {
        cleanup();
        if (runtimeReady()) {
          resolve();
          return;
        }
        fail('PROOF_STORIES_RUNTIME_INITIALIZATION_FAILED');
      };
      const handleError = () => fail('PROOF_STORIES_RUNTIME_LOAD_FAILED');

      script.addEventListener('load', handleLoad, { once: true });
      script.addEventListener('error', handleError, { once: true });
      timeoutId = window.setTimeout(
        () => fail('PROOF_STORIES_RUNTIME_LOAD_TIMEOUT'),
        15000
      );

      if (!pending) {
        script.src = source;
        script.async = true;
        script.dataset.tanProofRuntime = 'true';
        document.head.append(script);
      }
    });

    return runtimeRequest;
  };

  const attach = (root) => {
    if (!(root instanceof HTMLElement) || ROOT_STATES.has(root)) return;

    const abortController = new AbortController();
    const state = {
      abortController,
      observer: null,
      quietCleanup: null,
      statsObserver: null,
      statsFrame: 0,
      statsStarted: false,
      statsMotionQuery: null,
      statsMotionHandler: null,
    };
    ROOT_STATES.set(root, state);
    setupStats(root, state);

    const cancelQuietActivation = () => {
      state.quietCleanup?.();
      state.quietCleanup = null;
    };
    const activate = () => {
      state.observer?.disconnect();
      cancelQuietActivation();
      return loadRuntime(root);
    };
    const activateAfterScrollQuiet = () => {
      if (runtimeReady() || state.quietCleanup) return;
      state.observer?.disconnect();
      prefetchRuntime(root);

      const scrollTargets = scrollEventTargets();
      let timerId = 0;
      let settled = false;
      const cleanup = () => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timerId);
        scrollTargets.forEach((target) => target.removeEventListener('scroll', postpone));
        document.removeEventListener('visibilitychange', handleVisibility);
        abortController.signal.removeEventListener('abort', cleanup);
        if (state.quietCleanup === cleanup) state.quietCleanup = null;
      };
      const run = () => {
        if (abortController.signal.aborted || !root.isConnected) {
          cleanup();
          return;
        }
        if (document.visibilityState !== 'visible') {
          document.addEventListener('visibilitychange', handleVisibility, { once: true });
          return;
        }
        cleanup();
        void loadRuntime(root).catch(() => {});
      };
      const postpone = () => {
        window.clearTimeout(timerId);
        timerId = window.setTimeout(run, SCROLL_QUIET_MS);
      };
      const handleVisibility = () => {
        if (document.visibilityState === 'visible') postpone();
      };

      scrollTargets.forEach((target) => target.addEventListener('scroll', postpone, { passive: true }));
      abortController.signal.addEventListener('abort', cleanup, { once: true });
      state.quietCleanup = cleanup;
      postpone();
    };
    root.addEventListener('pointerenter', activateAfterScrollQuiet, {
      passive: true,
      once: true,
      signal: abortController.signal,
    });
    root.addEventListener('focusin', () => void activate().catch(() => {}), {
      capture: true,
      once: true,
      signal: abortController.signal,
    });
    root.addEventListener(
      'click',
      (event) => {
        if (runtimeReady()) return;
        const control = event.target.closest?.('[data-proof-play]');
        if (!control || !root.contains(control)) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        void activate().then(() => {
          if (control.isConnected) control.click();
        }).catch(() => {});
      },
      { capture: true, signal: abortController.signal }
    );

    if (window.Shopify?.designMode || !('IntersectionObserver' in window)) {
      void activate().catch(() => {});
      return;
    }

    state.observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) activateAfterScrollQuiet();
      },
      { rootMargin: '600px 0px', threshold: 0 }
    );
    state.observer.observe(root);
  };

  const rootsWithin = (scope) => {
    if (!(scope instanceof Element || scope instanceof Document)) return [];
    const roots = Array.from(scope.querySelectorAll(ROOT_SELECTOR));
    if (scope instanceof Element && scope.matches(ROOT_SELECTOR)) roots.unshift(scope);
    return roots;
  };
  const scan = (scope = document) => rootsWithin(scope).forEach(attach);
  const disconnect = (root) => {
    const state = ROOT_STATES.get(root);
    state?.observer?.disconnect();
    state?.statsObserver?.disconnect();
    if (state?.statsFrame) cancelAnimationFrame(state.statsFrame);
    state?.statsMotionQuery?.removeEventListener?.('change', state.statsMotionHandler);
    state?.quietCleanup?.();
    state?.abortController?.abort();
    ROOT_STATES.delete(root);
  };

  window.TANProofStoriesLoader = Object.freeze({ scan });
  scan(document);
  document.addEventListener('shopify:section:load', (event) => scan(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    rootsWithin(event.target).forEach(disconnect);
  });
})();
